import React, { useCallback } from "react";
import { Formik, Field, Form, FormikHelpers, FieldProps } from "formik";
import { useTranslation } from "react-i18next";
import { FormControl, FormErrorMessage } from "@chakra-ui/form-control";
import { ModalFooter, Stack, Select, Textarea, Button } from "@chakra-ui/react";
import * as Yup from "yup";
import { Input } from "@chakra-ui/input";
import { Label } from "../../components/Label";
import { Comunication } from "../../types";
import { DatePicker } from "../DatePicker";
import { PrimayButton } from "../Button";

interface Values {
  id: string | null;
  data: Date;
  operator: string;
  comunication_direction: string;
  duration: string;
  type: string;
  note: string;
}

type ComunicationFormProps = {
  onSubmit: (data: any) => any;
  onCancel: () => any;
  comunication?: Comunication | null;
};

export const ComunicationForm: React.FC<ComunicationFormProps> = ({
  onSubmit,
  onCancel,
  comunication,
}) => {
  const { t } = useTranslation();

  const handleSubmit = useCallback(
    (values: Values, { setSubmitting }: FormikHelpers<Values>) => {
      setSubmitting(false);
      onSubmit(values);
    },
    [onSubmit]
  );

  return (
    <Formik
      initialValues={{
        id: comunication?.id ? comunication?.id : null,
        data: comunication?.data ? comunication?.data : new Date(),
        operator: comunication?.operator ? comunication?.operator : "",
        comunication_direction: comunication?.comunicationDirection
          ? comunication?.comunicationDirection
          : "",
        duration: comunication?.duration ? comunication?.duration : "",
        type: comunication?.type ? comunication?.type : "",
        note: comunication?.note ? comunication?.note : "",
      }}
      enableReinitialize
      onSubmit={handleSubmit}
      validationSchema={Yup.object().shape({
        data: Yup.string().required(
          t("screens.client.comunicationFormValidations.dataRequired")
        ),
        operator: Yup.string().required(
          t("screens.client.comunicationFormValidations.operatorRequired")
        ),
        comunication_direction: Yup.string().required(
          t(
            "screens.client.comunicationFormValidations.comunicationDirectionRequired"
          )
        ),
        duration: Yup.string().required(
          t("screens.client.comunicationFormValidations.durationRequired")
        ),
        type: Yup.string().required(
          t("screens.client.comunicationFormValidations.typeRequired")
        ),
        note: Yup.string().required(
          t("screens.client.comunicationFormValidations.noteRequired")
        ),
      })}
    >
      {(props) => (
        <Form
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Stack spacing={"1rem"} direction={["column", "row"]} mb={"20px"}>
            <Field id={"data"} name={"data"}>
              {({ field, form: { setFieldValue }, meta }: FieldProps) => (
                <FormControl isInvalid={Boolean(meta.error && meta.touched)}>
                  <Label fontWeight="900">
                    {t("screens.client.comunicationForm.data")}
                  </Label>
                  <DatePicker
                    // @ts-ignore
                    size="md"
                    dateFormat={"dd/MM/yyyy"}
                    selectedDate={
                      props.values.data
                        ? new Date(props.values.data)
                        : undefined
                    }
                    onChange={(date) => {
                      setFieldValue("data", date);
                    }}
                  />
                  <FormErrorMessage>{meta.error}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
            <Field id={"operator"} name={"operator"}>
              {({ field, form: { setFieldValue }, meta }: FieldProps) => (
                <FormControl isInvalid={Boolean(meta.error && meta.touched)}>
                  <Label fontWeight="900">
                    {t("screens.client.comunicationForm.operator")}
                  </Label>
                  <Input
                    size="md"
                    type={"text"}
                    name={field.name}
                    value={field.value}
                    onChange={(event) =>
                      setFieldValue(field.name, event.target.value)
                    }
                  />
                  <FormErrorMessage>{meta.error}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
          </Stack>
          <Stack spacing={"1rem"} direction={["column", "row"]} mb={"20px"}>
            <Field
              id={"comunication_direction"}
              name={"comunication_direction"}
            >
              {({ field, form: { setFieldValue }, meta }: FieldProps) => (
                <FormControl isInvalid={Boolean(meta.error && meta.touched)}>
                  <Label fontWeight="900">
                    {t(
                      "screens.client.comunicationForm.comunication_direction"
                    )}
                  </Label>
                  <Select
                    placeholder="Seleziona..."
                    size="md"
                    name={field.name}
                    value={field.value}
                    onChange={(event) =>
                      setFieldValue(field.name, event.target.value)
                    }
                  >
                    <option value="In Entrata (chiamata ricevuta)">
                      In Entrata (chiamata ricevuta)
                    </option>
                    <option value="In Uscita (chiamata effettuata)">
                      In Uscita (chiamata effettuata)
                    </option>
                  </Select>
                  <FormErrorMessage>{meta.error}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
            <Field id={"duration"} name={"duration"}>
              {({ field, form: { setFieldValue }, meta }: FieldProps) => (
                <FormControl isInvalid={Boolean(meta.error && meta.touched)}>
                  <Label fontWeight="900">
                    {t("screens.client.comunicationForm.duration")}
                  </Label>
                  <Input
                    size="md"
                    type={"number"}
                    name={field.name}
                    value={field.value}
                    onChange={(event) =>
                      setFieldValue(field.name, event.target.value)
                    }
                  />
                  <FormErrorMessage>{meta.error}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
          </Stack>
          <Stack spacing={"1rem"} direction={["column", "row"]} mb={"20px"}>
            <Field id={"type"} name={"type"}>
              {({ field, form: { setFieldValue }, meta }: FieldProps) => (
                <FormControl isInvalid={Boolean(meta.error && meta.touched)}>
                  <Label fontWeight="900">
                    {t("screens.client.comunicationForm.type")}
                  </Label>
                  <Select
                    placeholder="Seleziona..."
                    size="md"
                    name={field.name}
                    value={field.value}
                    onChange={(event) =>
                      setFieldValue(field.name, event.target.value)
                    }
                  >
                    <option value="Consulenza iniziale">
                      Consulenza iniziale
                    </option>
                    <option value="Coaching">Coaching</option>
                    <option value="Valutazione risultati">
                      Valutazione risultati
                    </option>
                    <option value="Supporto tecnico">Supporto tecnico</option>
                    <option value="Altro">Altro</option>
                  </Select>
                  <FormErrorMessage>{meta.error}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
          </Stack>
          <Stack spacing={"1rem"} direction={["column", "row"]} mb={"20px"}>
            <Field id={"note"} name={"note"}>
              {({ field, form: { setFieldValue }, meta }: FieldProps) => (
                <FormControl isInvalid={Boolean(meta.error && meta.touched)}>
                  <Label fontWeight="900">
                    {t("screens.client.comunicationForm.note")}
                  </Label>
                  <Textarea
                    placeholder={t(
                      "screens.client.comunicationForm.noteTextAreaPlaceholder"
                    )}
                    size="md"
                    type={"text"}
                    name={field.name}
                    value={field.value}
                    onChange={(event) =>
                      setFieldValue(field.name, event.target.value)
                    }
                  />
                  <FormErrorMessage>{meta.error}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
          </Stack>
          <ModalFooter>
            <Button type={"reset"} variant={"ghost"} onClick={onCancel}>
              {t("common.reset")}
            </Button>

            <PrimayButton type={"submit"}>{t("common.save")}</PrimayButton>
          </ModalFooter>
        </Form>
      )}
    </Formik>
  );
};
